import { env } from "@p81-common/shell-core-js";

import { isSubscriptionActive } from "./isSubscriptionActive";

/**
 * Single source of truth for the unified Get Started (v2) flow toggle.
 *
 * Runtime-only flag delivered via /envs.js (must be REACT_APP_-prefixed or the
 * env-file generator strips it). When true, the app uses the v2 onboarding flow
 * (getStartedRefresh → onboarding-v2 + WizardDocument); when false/absent it
 * keeps the legacy v1 flow (getStartedProgressGet → onboarding/get-started).
 */
export const isNewGetStartedEnabled = (): boolean =>
  String(env.REACT_APP_NEW_GET_STARTED_ENABLE) === "true";

/**
 * Whether the app-shell loading gate for the Get Started flow is "settled", so
 * the shell can render instead of holding on the spinner.
 *
 * For the v2 flow the refresh (`getStartedRefreshRequest`) is intentionally
 * never dispatched without an active subscription (P81-110813). The gate must
 * mirror that skip, otherwise it waits forever for a request that is never sent
 * and no-subscription tenants are stuck on an infinite loader (P81-130117).
 * Three things therefore count as resolved for the v2 path:
 *  - there is no active subscription (nothing will be fetched), or
 *  - the refresh has loaded, or
 *  - the refresh failed (the Get Started page surfaces a retryable error).
 *
 * For an active subscription the first term is `false`, so the gate behaves
 * exactly as before (waits for load-or-error). The v1 path is unchanged: it
 * resolves once `getStartedProgressGet` has loaded.
 */
export const isOnboardingGateResolved = ({
  newGetStarted,
  subscriptionStatus,
  refreshLoaded,
  refreshError,
  legacyLoaded,
}: {
  newGetStarted: boolean;
  subscriptionStatus?: string;
  refreshLoaded?: boolean;
  refreshError?: unknown;
  legacyLoaded?: boolean;
}): boolean => {
  if (!newGetStarted) {
    return Boolean(legacyLoaded);
  }
  return (
    !isSubscriptionActive(subscriptionStatus ?? "") ||
    Boolean(refreshLoaded) ||
    Boolean(refreshError)
  );
};
